import React, { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Typography } from "@mui/material";
import styled from "@emotion/styled";
import DashboardEditor from "../components/DashboardEditor";
import WidgetRegistry from "../widgets/WidgetRegistry";
import { updateUser } from "../redux/slices/users";
import { setUserPreferences } from "../redux/slices/auth";
import { getRole } from "../redux/ducks/role";
import { FetchQueueProvider } from "../context/FetchQueueContext";
import { usePageTitle } from "../utils/brandUtils";

const DashboardContainer = styled(Box)(({ theme }) => ({
    width: "100%",
    maxWidth: "1920px",
    margin: "0 auto",
    padding: theme.spacing(3),
}));

const Dashboard = () => {
    usePageTitle("Dashboard");

    const dispatch = useDispatch();

    const currentUser = useSelector((state) => state.auth.currentUser);
    const userId = useSelector((state) => state.auth.userId);
    const permissions = useSelector((state) => state.auth.permissions) || [];

    const [layout, setLayout] = useState([]);
    const [widgets, setWidgets] = useState({});
    const [isLocked, setIsLocked] = useState(true);
    const [isInitialized, setIsInitialized] = useState(false);

    const saveTimerRef = useRef(null);
    const lastSavedLayoutRef = useRef(null);

    useEffect(() => () => {
        if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    }, []);

    useEffect(() => {
        if (isInitialized || !currentUser || !userId || !Array.isArray(permissions)) return;

        const dashboardPrefs = currentUser.preferences?.dashboard;
        const hasValidLayout = dashboardPrefs
            && Array.isArray(dashboardPrefs.layout)
            && dashboardPrefs.layout.length > 0
            && dashboardPrefs.layout.every((item) => item.i && item.widgetType !== undefined);
        const hasValidWidgets = dashboardPrefs
            && dashboardPrefs.widgets
            && typeof dashboardPrefs.widgets === "object"
            && Object.keys(dashboardPrefs.widgets).length > 0;

        if (hasValidLayout && hasValidWidgets) {
            setLayout(dashboardPrefs.layout);
            setWidgets(dashboardPrefs.widgets);
            lastSavedLayoutRef.current = dashboardPrefs.layout;
            setIsInitialized(true);
            return;
        }

        const applyDefault = (roleDefault) => {
            const defaultLayout = Array.isArray(roleDefault?.layout) ? roleDefault.layout : [];
            const defaultWidgets = (roleDefault?.widgets && typeof roleDefault.widgets === "object")
                ? roleDefault.widgets
                : {};
            setLayout(defaultLayout);
            setWidgets(defaultWidgets);
            lastSavedLayoutRef.current = defaultLayout;
            setIsInitialized(true);
        };

        const roleId = currentUser.role?._id;
        if (!roleId) {
            applyDefault(null);
            return;
        }

        let settled = false;
        const fallbackTimer = setTimeout(() => {
            if (!settled) {
                settled = true;
                applyDefault(null);
            }
        }, 5000);
        dispatch(getRole(roleId, (roleDoc) => {
            if (settled) return;
            settled = true;
            clearTimeout(fallbackTimer);
            applyDefault(roleDoc?.defaultDashboard);
        }));
    }, [currentUser, userId, isInitialized, permissions, dispatch]);

    const saveDashboardPreferences = useCallback((layoutToSave, widgetsToSave) => {
        if (!userId) return;

        const dashboardPrefs = { layout: layoutToSave, widgets: widgetsToSave };
        const preferencesToSave = {
            ...(currentUser.preferences || {}),
            dashboard: dashboardPrefs,
        };

        dispatch(setUserPreferences({ preferences: preferencesToSave }));
        dispatch(updateUser({
            userId,
            userData: { preferences: preferencesToSave },
            silent: true,
        }));
    }, [userId, currentUser, dispatch]);

    const debouncedSave = useCallback((newLayout, newWidgets) => {
        if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
        saveTimerRef.current = setTimeout(() => {
            saveDashboardPreferences(newLayout, newWidgets);
        }, 2000);
    }, [saveDashboardPreferences]);

    const handleEditorChange = useCallback(({ layout: newLayout, widgets: newWidgets }) => {
        setLayout(newLayout);
        setWidgets(newWidgets);
    }, []);

    const handleEditorCommit = useCallback(({ layout: newLayout, widgets: newWidgets }) => {
        const lastSaved = lastSavedLayoutRef.current || [];
        const sig = (l) => JSON.stringify(
            l.map((item) => ({ i: item.i, x: item.x, y: item.y, w: item.w, h: item.h }))
                .sort((a, b) => a.i.localeCompare(b.i))
        );
        const widgetsChanged = Object.keys(widgets).length !== Object.keys(newWidgets).length
            || Object.keys(newWidgets).some((k) => !widgets[k]);

        if (sig(lastSaved) !== sig(newLayout) || widgetsChanged) {
            lastSavedLayoutRef.current = newLayout;
            debouncedSave(newLayout, newWidgets);
        }
    }, [debouncedSave, widgets]);

    const handleToggleLock = useCallback(() => setIsLocked((prev) => !prev), []);

    const handleWidgetConfigChange = useCallback((instanceId, patch) => {
        const existing = widgets[instanceId];
        if (!existing) return;

        const nextWidgets = { ...widgets, [instanceId]: { ...existing, config: { ...(existing.config || {}), ...patch } } };
        setWidgets(nextWidgets);
        saveDashboardPreferences(layout, nextWidgets);
    }, [widgets, layout, saveDashboardPreferences]);

    const validLayoutItems = useMemo(() => layout.filter((item) => {
        const widgetData = widgets[item.i];
        if (!widgetData) return false;
        const WidgetComponent = WidgetRegistry.getComponent(widgetData.type);
        if (!WidgetComponent) return false;
        return WidgetRegistry.isWidgetAvailable(widgetData.type, permissions);
    }), [layout, widgets, permissions]);

    const layoutOrder = useMemo(() => [...validLayoutItems]
        .sort((a, b) => (a.y !== b.y ? a.y - b.y : a.x - b.x))
        .map((item) => item.i),
    [validLayoutItems]);

    if (!isInitialized) {
        return (
            <DashboardContainer>
                <Typography>Loading dashboard...</Typography>
            </DashboardContainer>
        );
    }

    return (
        <FetchQueueProvider layoutOrder={layoutOrder}>
            <DashboardContainer>
                <DashboardEditor
                    layout={layout}
                    widgets={widgets}
                    isLocked={isLocked}
                    permissions={permissions}
                    onChange={handleEditorChange}
                    onCommit={handleEditorCommit}
                    onToggleLock={handleToggleLock}
                    onWidgetConfigChange={handleWidgetConfigChange}
                />
            </DashboardContainer>
        </FetchQueueProvider>
    );
};

export default Dashboard;
